// Need to use the React-specific entry point to allow generating React hooks
import GuardianAuth from '@amzn/GuardianAuth/lib/auth/GuardianAuth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';
import { CreateConsultationVars, EMTCase, MARSCase } from '../lib/CaseDefinitions';
import { CaseFilter, ConsultationFilter } from '../lib/FilterDefinitons';
import {
    REACT_APP_WOTMAN_SERVICE_ENDPOINT,
    GET_FACILITIES_ENDPOINT,
    GET_FACILITY_DETAILS_ENDPOINT,
    GET_ALL_THREAT_MANAGERS_ENDPOINT,
    SEARCH_INCIDENT_NAME_ENDPOINT,
    LINK_CASE_CONSULTATION_ENDPOINT,
    GET_CONSULTATION_DETAILS_ENDPOINT,
    GET_CASE_DETAILS_ENDPOINT,
    EDIT_RECOMMENDATIONS_ENDPOINT,
    EDIT_CONSULTATION_ENDPOINT,
    SEARCH_CONSULTATION_NAME_ENDPOINT,
    BOOKMARK_CASE_ENDPOINT,
    LINK_CASES_ENDPOINT,
    ADD_MANUAL_UPDATE_ENDPOINT,
    HAS_CONSULTATION_NAME_ENDPOINT,
    SEARCH_FOR_EMPLOYEES,
    GET_EMPLOYEE_DETAILS,
    SEARCH_CONSULTATIONS_ENDPOINT,
    SEARCH_CASES_ENDPOINT,
    EDIT_LPCONTACT_ENDPOINT,
    EDIT_MARS_CASE_ENDPOINT,
    SEARCH_CASE_NAME_CONSULTATION_NAME_ENDPOINT,
    CREATE_CONSULTATION_ENDPOINT,
    CREATE_INCIDENT_ENDPOINT,
    ACKNOWLEDGE_UPDATE_ENDPOINT,
    GET_ALL_SELECT_OPTIONS,
    ADD_EDIT_CONTACT_ENDPOINT,
    GET_FILE_DATA,
    UPDATE_CASE_ENDPOINT,
    SEARCH_SIM_ENDPOINT,
    LINK_SIM_ENDPOINT,
    GET_USER_CONTEXT
} from '../lib/globals';
import { SimStatusTypes } from '../lib/UpdateDefinitions';

const transformCase = (response: MARSCase | EMTCase) => {
    response.linkedTickets.sort((a, b) => {
        if (a.status === SimStatusTypes.OPEN) {
            if (b.status !== SimStatusTypes.OPEN) {
                return -1;
            }
        } else if (b.status === SimStatusTypes.OPEN) {
            return 1;
        }
        return DateTime.fromISO(a.monitoringEndDate) > DateTime.fromISO(b.monitoringEndDate) ? 1 : -1;
    });
    return response;
};

// Define a service using a base URL and expected endpoints
export const wotManServiceApi = createApi({
    reducerPath: 'wotManServiceApi',
    baseQuery: fetchBaseQuery({ baseUrl: REACT_APP_WOTMAN_SERVICE_ENDPOINT }),
    endpoints: (build) => ({
        getFacilities: build.query<string[], Record<string, string>>({
            query: (params: { prefix: string }) => ({
                url: GET_FACILITIES_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getFacilityDetails: build.query<string[], Record<string, string | string[]>>({
            query: (params: { facilities: string[] }) => ({
                url: GET_FACILITY_DETAILS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getSelectOptions: build.query<string[], Record<string, string>>({
            query: (params: { prefix: string }) => ({
                url: GET_ALL_SELECT_OPTIONS,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getUserContext: build.query<string[], Record<string, string>>({
            query: (params: { prefix: string }) => ({
                url: GET_USER_CONTEXT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getThreatManagers: build.query<string[], Record<string, string | string[]>>({
            query: () => ({
                url: GET_ALL_THREAT_MANAGERS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader()
            })
        }),
        searchIncidentName: build.query<string[], Record<string, string | string[]>>({
            query: (params: { prefix: string }) => ({
                url: SEARCH_INCIDENT_NAME_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        searchForEmployees: build.query<string[], Record<string, string | string[]>>({
            query: (params: { prefix: string }) => ({
                url: SEARCH_FOR_EMPLOYEES,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getEmployeeDetails: build.query<string[], Record<string, string | string[]>>({
            query: (params: { personId: string }) => ({
                url: GET_EMPLOYEE_DETAILS,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        searchConsultationName: build.query<string[], Record<string, string | string[]>>({
            query: (params: { prefix: string }) => ({
                url: SEARCH_CONSULTATION_NAME_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        hasConsultationName: build.query<string[], Record<string, string | string[]>>({
            query: (params: { name: string }) => ({
                url: HAS_CONSULTATION_NAME_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getConsultationDetails: build.query<string[], Record<string, string | number>>({
            query: (params: { consultationId: number }) => ({
                url: GET_CONSULTATION_DETAILS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        getCaseDetails: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            query: (params: { incidentId: number }) => ({
                url: GET_CASE_DETAILS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        linkCaseConsultation: build.query<string[], Record<string, string | number>>({
            query: (params: { caseId: number; consultationId: number; action: string }) => ({
                url: LINK_CASE_CONSULTATION_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        linkCaseSIM: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            query: (params: { caseId: number; simId: string; action: string }) => ({
                url: LINK_SIM_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        editRecommendations: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            query: (params: { caseId: number; consultationId: number; action: string }) => ({
                url: EDIT_RECOMMENDATIONS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        editLPContact: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            query: (params: { incidentId: number; personId: string; note: string }) => ({
                url: EDIT_LPCONTACT_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        updateCase: build.query<MARSCase | EMTCase, Record<string, string | number | undefined>>({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            query: (params: { updating: any }) => ({
                url: UPDATE_CASE_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        editConsultation: build.query<string[], Record<string, CreateConsultationVars | number>>({
            query: (params: { consultation: CreateConsultationVars; consultationId: number }) => ({
                url: EDIT_CONSULTATION_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        bookmarkCase: build.query<MARSCase | EMTCase, Record<string, boolean | number>>({
            query: (params: { caseId: number; isBookmarking: boolean }) => ({
                url: BOOKMARK_CASE_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        linkCases: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            query: (params: { caseIdOne: number; caseIdTwo: number; action: string }) => ({
                url: LINK_CASES_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        addManualUpdate: build.query<string[], Record<string, number>>({
            query: (params: { incidentId: number; updateId: number }) => ({
                url: ADD_MANUAL_UPDATE_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        acknowledgeUpdate: build.query<MARSCase | EMTCase, Record<string, number>>({
            query: (params: { incidentId: number; updateId: number }) => ({
                url: ACKNOWLEDGE_UPDATE_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        searchCases: build.query<string[], Record<string, CaseFilter>>({
            query: (params: { caseFilter: CaseFilter }) => ({
                url: SEARCH_CASES_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        searchConsultations: build.query<string[], Record<string, ConsultationFilter>>({
            query: (params: { consultationFilter: ConsultationFilter }) => ({
                url: SEARCH_CONSULTATIONS_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        editMarsCase: build.query<MARSCase | EMTCase, Record<string, ConsultationFilter>>({
            query: (params: { consultationFilter: ConsultationFilter }) => ({
                url: EDIT_MARS_CASE_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),

        searchCaseNameConsultationName: build.query<string[], Record<string, string>>({
            query: (params: { query: string }) => ({
                url: SEARCH_CASE_NAME_CONSULTATION_NAME_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        createConsultation: build.query<string[], Record<string, string | number | undefined>>({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            query: (params: { consultationCreating: any }) => ({
                url: CREATE_CONSULTATION_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        createCase: build.query<string[], Record<string, string | number | undefined>>({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            query: (params: { incidentCreating: any }) => ({
                url: CREATE_INCIDENT_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        addEditContact: build.query<MARSCase | EMTCase, Record<string, string | number>>({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            query: (params: { personInvolved: any; personId: string }) => ({
                url: ADD_EDIT_CONTACT_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            }),
            transformResponse: transformCase
        }),
        getFileData: build.query<string[], Record<string, string>>({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            query: (params: { fileId: string }) => ({
                url: GET_FILE_DATA,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        }),
        searchSIMs: build.query<string[], Record<string, string | string[]>>({
            query: (params: { prefix: string }) => ({
                url: SEARCH_SIM_ENDPOINT,
                method: 'POST',
                headers: GuardianAuth.createRequestAuthHeader(),
                body: JSON.stringify(params)
            })
        })
    })
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
// can't export like this as typescript version is not 4.1
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetFacilitiesQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetFacilityDetailsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useHasConsultationNameQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useSearchIncidentNameQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazySearchForEmployeesQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetEmployeeDetailsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useSearchConsultationNameQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useGetThreatManagersQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyLinkCaseConsultationQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetConsultationDetailsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetCaseDetailsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyEditRecommendationsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyEditLPContactQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyUpdateLocQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyUpdateCaseQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyAcknowledgeUpdateQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyEditConsultationQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyLinkCasesQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyBookmarkCaseQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyAddManualUpdateQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazySearchCasesQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyCreateConsultationQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyCreateCaseQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazySearchConsultationsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyEditMarsCaseQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazySearchCaseNameConsultationNameQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useGetUserContextQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyAddEditContactQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyGetFileDataQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazySearchSIMsQuery,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useLazyLinkCaseSIMQuery
} = wotManServiceApi;
